import { BytesSizeUnit, FileExtensions } from '@philips-internal/oswald-ui-constants';
import { AuthenticationService, HttpHeader, MediaType } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { convertBytesSize, enumValues, generateUUID } from '@philips-internal/oswald-ui-utils';
/**
 * This method is responsible for applying an JSON media type accept header to a request.
 *
 * @example
 * ```
 * applyAcceptHeader(request);
 * ```
 *
 * @param request - The XMLHttpRequest object to apply the header;
 */
function applyAcceptHeader(request) {
    request.setRequestHeader(HttpHeader.ACCEPT, MediaType.APPLICATION_JSON);
}
/**
 * This method is responsible for applying an authorization header to a request.
 *
 * @example
 * ```
 * applyAuthorizationHeader(request);
 * ```
 *
 * @param request - The XMLHttpRequest object to apply the header;
 */
function applyAuthorizationHeader(request) {
    const token = AuthenticationService.getJWTToken();
    if (token) {
        request.setRequestHeader(HttpHeader.AUTHORIZATION, `${token.getType()} ${token.rawToken()}`);
    }
}
/**
 * This method triggers a file download event based ona link element click and also returns this file.
 *
 * @example
 * ```
 * download(file, 'file01');
 * ```
 *
 * @param file - The options object to get the file interface;
 * @param name - The file name to appears when the file is downloaded;
 *
 * @returns - The file object
 */
function download(file, name) {
    const blob = new Blob([file]);
    const anchor = document.createElement('a');
    anchor.download = name ?? generateUUID();
    anchor.href = URL.createObjectURL(blob);
    anchor.click();
    URL.revokeObjectURL(anchor.href);
    return blob;
}
/**
 * This method is responsible for returning a FileInterface object based on the informed options.
 *
 * @example
 * ```
 * getFileInterface({ fileName: file.name, percentage: 0, request, state: 'invalid' });
 * ```
 *
 * @param options - The options object to get the file interface;
 *
 * @returns - The FileInterface object
 */
function getFileInterface(options) {
    const { fileName, path, percentage, request, state } = options;
    const uploadProgress = { fileName, path, percentage, state };
    const onCancel = () => {
        if (request?.readyState === XMLHttpRequest.UNSENT) {
            return;
        }
        request?.abort();
    };
    return {
        onCancel,
        uploadProgress,
    };
}
/**
 * This method is responsible for returning if a file matches the allowed file extensions.
 *
 * @example
 * ```
 * isAllowedFile(file);
 * ```
 *
 * @param file - The file to be checked
 *
 */
function isAllowedFile(file) {
    const [, extension] = file.name.match(/\.([^.]+)$/) ?? [];
    if (!extension) {
        return false;
    }
    return !enumValues(FileExtensions).includes(extension.toLowerCase());
}
/**
 * This method is responsible for returning if a file matches the allowed file size.
 *
 * @example
 * ```
 * isAllowedFileSize(file, { maxSize: 2, sizeUnit: BytesSizeUnit.MEGABYTES });
 * ```
 *
 * @param file - The options object to get the file interface;
 * @param maxFileSize - The object containing the maximum size of the file and the size unit;
 *
 */
function isAllowedFileSize(file, maxFileSize) {
    const isAllowedFileSize = maxFileSize ? convertBytesSize(file.size ?? 0, BytesSizeUnit.BYTES, maxFileSize.sizeUnit) <= maxFileSize.maxSize : true;
    return isAllowedFileSize;
}
export { applyAcceptHeader, applyAuthorizationHeader, download, isAllowedFile, isAllowedFileSize, getFileInterface, };
//# sourceMappingURL=file-service-utils.js.map